// A session's items as the transcript lays them out: a sub-agent's calls
// under the call that started it, a run of actions folded into one row, and
// what a call says in one line. Ported from today's Code tab
// (code/items.ts there), its presentation part: the items themselves come
// from the ACP adapter (acp.ts). Nothing here reads the page or the server.
import type { Item, ToolItem } from './types'

// --- rows ------------------------------------------------------------------------

/** An item as the transcript lays it out: a sub-agent's calls stand under the call that runs them. */
export interface Row {
  item: Item
  children: Row[]
}

/**
 * The rows of a transcript. A tool call whose `parent` is on the page goes
 * under it, however deep; one whose parent is not stands by itself.
 */
export function toRows(items: readonly Item[]): Row[] {
  const byCall = new Map<string, Row>()
  const rows: Row[] = []
  for (const item of items) {
    const row: Row = { item, children: [] }
    if (item.kind === 'tool') {
      const parent = item.parent ? byCall.get(item.parent) : undefined
      byCall.set(item.tool_use_id, row)
      if (parent) {
        parent.children.push(row)
        continue
      }
    }
    rows.push(row)
  }
  return rows
}

/**
 * The reply that ends each turn: the last thing the agent said before the
 * turn's end or the next prompt. It is the one a reader copies, so it alone
 * has the copy icon; what is said between tool calls has none. While a turn
 * is under way (`busy`) its latest text is not yet its last: more may follow.
 */
export function lastReplies(items: readonly Item[], busy = false): ReadonlySet<string> {
  const found = new Set<string>()
  let last: string | null = null
  for (const item of items) {
    if (item.kind === 'assistant') last = item.id
    else if (item.kind === 'user' || item.kind === 'result') {
      if (last !== null) found.add(last)
      last = null
    }
  }
  if (last !== null && !busy) found.add(last)
  return found
}

/** A run of actions this long or longer shows as one row that opens; a shorter run stands as it is. */
export const GROUP_FROM = 3

/** What the transcript lays out: a row, or a run of the session's actions folded into one. */
export type Block = { kind: 'row'; row: Row } | { kind: 'group'; rows: Row[] }

const isAction = (row: Row) => row.item.kind === 'tool' || row.item.kind === 'thinking'

/**
 * The blocks of a transcript's rows: `GROUP_FROM` or more tool calls and
 * thoughts in a row are one group, so the replies read without the work
 * between them. Anything else (a prompt, a reply, a plan, a notice, a turn's
 * end) stands by itself and ends a run.
 */
export function toBlocks(rows: readonly Row[]): Block[] {
  const blocks: Block[] = []
  let run: Row[] = []
  const flush = () => {
    if (run.length >= GROUP_FROM) blocks.push({ kind: 'group', rows: run })
    else for (const row of run) blocks.push({ kind: 'row', row })
    run = []
  }
  for (const row of rows) {
    if (isAction(row)) {
      run.push(row)
      continue
    }
    flush()
    blocks.push({ kind: 'row', row })
  }
  flush()
  return blocks
}

/**
 * Poulpe's own MCP tools: the board's. The runner names its server `poulpe`,
 * or `poulpe_next` for an agent whose own configuration has a `poulpe`
 * already (crates/poulpe-runner/src/mcp.rs).
 */
const BOARD_TOOL = /^mcp__poulpe(?:_next)?__/

/** What a group of actions did, by kind, in the order they read: `8 commands`, `3 board calls`. */
export function actionCounts(rows: readonly Row[]): string[] {
  let commands = 0
  let edits = 0
  let board = 0
  let other = 0
  let thoughts = 0
  for (const { item } of rows) {
    if (item.kind === 'thinking') thoughts += 1
    else if (item.kind !== 'tool') continue
    else if (item.edits.length > 0) edits += 1
    else if (item.command) commands += 1
    else if (BOARD_TOOL.test(item.name)) board += 1
    else other += 1
  }
  const parts: string[] = []
  const put = (count: number, singular: string) => count > 0 && parts.push(`${count} ${count === 1 ? singular : `${singular}s`}`)
  put(commands, 'command')
  put(edits, 'edit')
  put(board, 'board call')
  put(other, parts.length > 0 ? 'other call' : 'call')
  put(thoughts, 'thought')
  return parts
}

/** How many calls stand under a row, at every depth. */
export function callCount(row: Row): number {
  return row.children.reduce((sum, child) => sum + 1 + callCount(child), 0)
}

// --- what a tool call says in one line ----------------------------------------------

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

/** `path` without the session's directory before it, when it is under it. */
export function relativePath(path: string, cwd: string | null | undefined): string {
  if (!cwd) return path
  const base = cwd.endsWith('/') ? cwd : `${cwd}/`
  return path.startsWith(base) && path.length > base.length ? path.slice(base.length) : path
}

/** A tool's name as it reads: an MCP tool is `server · tool`. */
export function toolName(name: string): string {
  const mcp = /^mcp__(.+?)__(.+)$/.exec(name)
  return mcp ? `${mcp[1]} · ${mcp[2]}` : name
}

/** The input fields that say what a call is about, in the order they are looked for: Poulpe's tools name an issue as `id` or `issue`. */
const SUMMARY_FIELDS = ['file_path', 'notebook_path', 'pattern', 'path', 'query', 'url', 'description', 'skill', 'subagent_type', 'prompt', 'name', 'id', 'issue', 'arguments'] as const

/**
 * What a call is about, on one line: the command, the path it edits, the
 * field of its input that names its subject, or, for a call given no input,
 * what its agent said it is about; '' when none does.
 */
export function toolSummary(item: Pick<ToolItem, 'command' | 'edits' | 'input' | 'subject'>, cwd?: string | null): string {
  if (item.command) return item.command
  const [edit] = item.edits
  if (edit) return relativePath(edit.path, cwd)
  const input = record(item.input)
  for (const field of SUMMARY_FIELDS) {
    const value = input[field]
    if (typeof value !== 'string' || value.trim() === '') continue
    const text = field === 'file_path' || field === 'notebook_path' || field === 'path' ? relativePath(value, cwd) : value
    // A pattern is looked for somewhere: say where when the call does.
    if (field === 'pattern' && typeof input.path === 'string' && input.path) return `${text} in ${relativePath(input.path, cwd)}`
    return text.replace(/\s+/g, ' ').trim()
  }
  if (Array.isArray(input.todos)) return `${input.todos.length} ${input.todos.length === 1 ? 'item' : 'items'}`
  if (item.subject) {
    // A path is read from the session's folder, wherever the agent wrote it.
    const base = cwd ? (cwd.endsWith('/') ? cwd : `${cwd}/`) : null
    return (base ? item.subject.split(base).join('') : item.subject).replace(/\s+/g, ' ').trim()
  }
  return ''
}

/** An input as text to read: JSON, two spaces deep; a string as it is. */
export function inputText(input: unknown): string {
  if (input === undefined || input === null) return ''
  if (typeof input === 'string') return input
  try {
    return JSON.stringify(input, null, 2)
  } catch {
    return String(input)
  }
}

// --- diffs -------------------------------------------------------------------------

export type DiffLineKind = 'add' | 'del' | 'hunk' | 'meta' | 'context'

export interface DiffLine {
  kind: DiffLineKind
  text: string
}

/** The lines of a unified diff, each with what it is: added, removed, a hunk's header, the file's, or context. */
export function diffLines(diff: string): DiffLine[] {
  if (diff === '') return []
  const lines = diff.replace(/\n$/, '').split('\n')
  // A diff without hunk headers is all body: only its `---` and `+++` lines name the file.
  const bare = !lines.some((text) => text.startsWith('@@'))
  let inHunk = bare
  return lines.map((text): DiffLine => {
    if (text.startsWith('@@')) {
      inHunk = true
      return { kind: 'hunk', text }
    }
    if (text.startsWith('diff --git ')) {
      inHunk = bare
      return { kind: 'meta', text }
    }
    if (!inHunk || text.startsWith('\\ ') || (bare && /^(?:\+\+\+|---) /.test(text))) return { kind: 'meta', text }
    if (text.startsWith('+')) return { kind: 'add', text }
    if (text.startsWith('-')) return { kind: 'del', text }
    return { kind: 'context', text }
  })
}

/** How many lines a diff adds and removes. */
export function diffStat(diff: string): { additions: number; deletions: number } {
  let additions = 0
  let deletions = 0
  for (const line of diffLines(diff)) {
    if (line.kind === 'add') additions += 1
    else if (line.kind === 'del') deletions += 1
  }
  return { additions, deletions }
}
